import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData } from '../helpers/ApiSettings';

const DeliveryZones = () => {
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    cities: '',
    price: ''
  });

  const fetchDeliveryZones = async () => {
    try {
      setLoading(true);
      const response = await getData('/delivery-zones?sort[0]=name:asc');
      setDeliveryZones(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des zones de livraison');
      console.error('Error fetching delivery zones:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveryZones();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ensure price is a number
      const dataToSubmit = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (editingZone) {
        await putData(`/delivery-zones/${editingZone.documentId}`, { data: dataToSubmit });
        alert('Zone de livraison modifiée avec succès');
      } else {
        await postData('/delivery-zones', { data: dataToSubmit });
        alert('Zone de livraison créée avec succès');
      }
      
      // Close modal first
      const modal = document.getElementById('deliveryZoneModal');
      const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      bootstrapModal.hide();
      
      // Then reset form and fetch new data
      setFormData({ name: '', cities: '', price: '' });
      setEditingZone(null);
      await fetchDeliveryZones();
    } catch (err) {
      alert('Erreur lors de l\'enregistrement de la zone de livraison');
      console.error('Error saving delivery zone:', err);
    }
  };

  const handleEdit = (zone) => {
    setEditingZone(zone);
    setFormData({
      name: zone.name,
      cities: zone.cities,
      price: zone.price.toString()
    });
    // Fixed Bootstrap modal reference
    const modal = new window.bootstrap.Modal(document.getElementById('deliveryZoneModal'));
    modal.show();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette zone de livraison ?')) {
      try {
        await deleteData(`/delivery-zones/${id}`);
        await fetchDeliveryZones();
        alert('Zone de livraison supprimée avec succès');
      } catch (err) {
        alert('Erreur lors de la suppression de la zone de livraison');
        console.error('Error deleting delivery zone:', err);
      }
    }
  };

  const handleAddNew = () => {
    setEditingZone(null);
    setFormData({ name: '', cities: '', price: '' });
    // Fixed Bootstrap modal reference
    const modal = new window.bootstrap.Modal(document.getElementById('deliveryZoneModal'));
    modal.show();
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Zones de Livraison</h2>
        <div>
          <button 
            onClick={fetchDeliveryZones} 
            className="btn btn-outline-primary me-2"
          >
            Actualiser
          </button>
          <button 
            onClick={handleAddNew}
            className="btn btn-primary"
          >
            Nouvelle Zone
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {deliveryZones.length === 0 ? (
            <p className="text-center text-muted my-5">Aucune zone de livraison pour le moment</p>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Villes</th>
                    <th>Prix</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryZones.map((zone) => (
                    <tr key={zone.id}>
                      <td>{zone.name}</td>
                      <td>
                        <div style={{ maxWidth: '300px', whiteSpace: 'pre-wrap' }}>
                          {zone.cities}
                        </div>
                      </td>
                      <td>{zone.price.toFixed(2)}€</td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => handleEdit(zone)}
                          >
                            Modifier
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(zone.documentId)}
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Add/Edit Delivery Zone */}
      <div className="modal fade" id="deliveryZoneModal" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingZone ? 'Modifier la Zone de Livraison' : 'Nouvelle Zone de Livraison'}
                </h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">Nom de la zone</label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="cities" className="form-label">Villes</label>
                  <textarea
                    className="form-control"
                    id="cities"
                    name="cities"
                    value={formData.cities}
                    onChange={handleInputChange}
                    rows="4"
                    required
                  />
                  <small className="text-muted">
                    Entrez les noms des villes séparés par des virgules ou retours à la ligne
                  </small>
                </div>
                <div className="mb-3">
                  <label htmlFor="price" className="form-label">Prix de livraison</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                    <span className="input-group-text">€</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingZone ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryZones;